import { AppBar, Box, Button, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EmptyList,
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDropDownRHF,
	EnhancedSnackbar,
	RHookFormNumberField,
	RouterLinkButton,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { IGetListRequest, IWSIBOntarioRemittance } from "../../models";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
	WSIB_ONTARIO_LIST_SCREEN_SLUG,
} from "../../common/constants";
import { IResponse, IServerError } from "../../models/common";
import { Link, useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatDate, formatThousandSeperatorWithTwoFixedAmount, sortDDLByLabel, useNavigateToLogout } from "../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedDrawerMenu,
	setSelectedStickyNavBar,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminClientRoutesComponent } from "../RoleBasedLeftMenu/routes/AdminClientRoutes";
import { ClientRoutesComponent } from "../RoleBasedLeftMenu/routes/ClientRoutes";
import { CountryService } from "../../services/common/CountryService";
import FileSaver from "file-saver";
import { IAppState } from "../../store/reducers";
import { IDropDownListResponse } from "../../models/common/IDropDownListResponse";
import { PrimeDataGridWithColumnToggled } from "../PrimeDataGridWithColumnToggled";
import { WSIBOntarioRemittanceDataGrid } from "./WSIBOntarioRemittanceDataGrid";
import { WSIBOntarioRemittanceHeader } from "./WSIBOntarioRemittanceHeader"
import { WSIBOntarioService } from "../../services/WSIBOntarioService";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";

// import { CountryService } from "../../services/CountryService";

export interface IWSIBRemittanceSetup { }

const useWSIBOntarioSavedFrequency = (
	clientId: string,
	taxYear: number,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [
			'rq-remittance-saved-frequency',
		],
		queryFn: (data) => {

			return WSIBOntarioService.getWSIBOntarioSavedFrequency(
				clientId,
				taxYear,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetRemittanceFrequencyDDL = () =>
	useQuery({
		queryKey: ["remittance-frequency-ddl"],
		queryFn: (data) => {
			return WSIBOntarioService.getRemittanceFrequencyDDL();
		},
		refetchOnWindowFocus: false,
	});
const useGetSavedWSIBDDL = (clientId: string, provinceId: any) =>
	useQuery({
		queryKey: ["saved-wsib-ddl", clientId, provinceId],
		queryFn: (data) => {
			return WSIBOntarioService.getSavedWSIBDDL(clientId, provinceId);
		},
		refetchOnWindowFocus: false,
	});
const useGetDefaultCodeProfileDataById = (id: null | string) =>
	useQuery({
		queryKey: ["get-defaultCode-profile", id],
		queryFn: (data) => {
			return WSIBOntarioService.getDefaultCodeProfileDataById(id);
		},
		refetchOnWindowFocus: false,
	});

// const SINGLE_RECORD_DATA_QUERY_KEY = "code-setup-single-data-record";
// const useGetDefaultCodeProfileDataById = (
// 	clientId: string,
// 	id: string | undefined,
// 	navigateToLogout: (path: string) => void
// ) =>
// 	useQuery({
// 		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, id, navigateToLogout],
// 		queryFn: () => {
// 			return WSIBOntarioService.getCodeById(clientId, id, navigateToLogout);
// 		},
// 		refetchOnWindowFocus: false,
// 	});

const WSIB_ONTARIO_LIST_QUERY_KEY = "rq-ramittance-list";

const useWSIBOntarios = (
	remittanceFrequencyId: string | undefined,
	clientId: string,
	taxYear: number,
	loadedProfileId: string,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [
			WSIB_ONTARIO_LIST_QUERY_KEY,
			remittanceFrequencyId,
			clientId,
			taxYear,
			loadedProfileId,
			navigateToLogout
		],
		queryFn: (data) => {

			return WSIBOntarioService.getWSIBOntario(
				remittanceFrequencyId,
				clientId,
				taxYear,
				loadedProfileId,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetDefaultCodeDDL = (clientId: string, provinceId: any, taxYear: number) =>
	useQuery({
		queryKey: ["default-code-ddl", clientId, provinceId, taxYear],
		queryFn: (data) => {
			return WSIBOntarioService.getDefaultCodeDDL(clientId, provinceId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

// const useGetCodeDDL = (provinceId: any, taxYear: number) =>
// useQuery({
// 	queryKey: ["default-code-ddl"],
// 	queryFn: (data) => {
// 		return WSIBOntarioService.getCodesDDL(provinceId, taxYear);
// 	},
// 	refetchOnWindowFocus: false,
// });
export const WSIBOntarioRemittanceSetup: React.FC<IWSIBRemittanceSetup> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();
	const { AdminClientRoutes } = AdminClientRoutesComponent();
	const { ClientRoutes } = ClientRoutesComponent();

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [selectedRowsValues, setSelectedRowsValues] = useState([]);
	const [rqRamittanceSetupTableData, setWSIBOntarioSetupTableData] =
		useState([]);
	const [remittanceFrequencyId, setRemittanceFrequencyId] = useState(undefined);
	const [selectedDefaultCodeProfile, setSelectedDefaultCodeProfile] = useState<any>(null);

	const [loadedProfileId, setLoadedProfileId] = useState<any>(null);
	const canadaProvince = useSelector((state: IAppState) => state.countryAndProvincesInfo.canadaProvinces)
	const ontarioId = canadaProvince && Array.isArray(canadaProvince) && canadaProvince.find((x: any) => x.label === 'Ontario')?.value;
	const [isLoading, setIsLoading] = useState<any>(false);
	const formMethods = useForm<any>({
		// resolver: yupResolver(ValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});
	const remittanceFrequencyIdField = formMethods.getValues(
		"remittanceFrequencyId"
	);
	useEffect(() => {
		if (remittanceFrequencyIdField) {
			setRemittanceFrequencyId(remittanceFrequencyIdField);
		}
	}, [remittanceFrequencyIdField]);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const { isPending: isPendingWSIBOntarioSavedFrequency, data: wsibOntarioSavedFrequency }: any = useWSIBOntarioSavedFrequency(
		clientIdStore,
		taxYear,
		navigateToLogout
	);

	const {
		isPending: isPendingSavedWSIBDDL,
		data: savedWSIBDDL,
	} = useGetSavedWSIBDDL(clientIdStore, ontarioId);

	// useEffect(() => {
	// 	// if (wsibOntarioSavedFrequency && wsibOntarioSavedFrequency.frequencies !== null) {
	// 	if (wsibOntarioSavedFrequency) {
	// 		formMethods.setValue(
	// 			"remittanceFrequencyId",
	// 			wsibOntarioSavedFrequency.frequencies
	// 		);
	// 		setRemittanceFrequencyId(wsibOntarioSavedFrequency.frequencies);
	// 	}
	// }, [wsibOntarioSavedFrequency]);
	useEffect(() => {
		// if (wsibOntarioSavedFrequency && wsibOntarioSavedFrequency.frequencies !== null) {
		if (wsibOntarioSavedFrequency) {
			formMethods.setValue(
				"remittanceFrequencyId",
				wsibOntarioSavedFrequency.frequencies
			);
			setRemittanceFrequencyId(wsibOntarioSavedFrequency.frequencies);

			if (wsibOntarioSavedFrequency.frequencies && savedWSIBDDL && !loadedProfileId) {
				const latestItem = savedWSIBDDL.length
					? savedWSIBDDL
						.filter((item: any) => !(item.value === '' && item.label === '--'))
						.reduce((latest: any, item: any) =>
							Date.parse(item.label) > Date.parse(latest.label) ? item : latest
						)
					: null;

				if (latestItem) {
					formMethods.setValue(
						"savedWSIBDDL",
						latestItem.value
					);
					setLoadedProfileId(latestItem.value)
				}
			}
		}
	}, [wsibOntarioSavedFrequency, savedWSIBDDL]);

	const { id } = useSelector((state: IAppState) => state.accountant);
	const { isPending, data } = useWSIBOntarios(
		remittanceFrequencyId,
		clientIdStore,
		taxYear,
		loadedProfileId,
		navigateToLogout
	); //useQuery('accountants-list', AccountantService.getAccountantList, { refetchOnWindowFocus: false });
	const wsibOntarioRemittanceSetup: any | null = data;


	const { isPending: pendingDefaultCodeProfileDAta, data: defaultCodeProfileData } = useGetDefaultCodeProfileDataById(
		loadedProfileId);

	useEffect(() => {
		if (defaultCodeProfileData) {
			const effectiveDate = new Date(defaultCodeProfileData.effectiveDate)
			formMethods.setValue("effectiveDate", effectiveDate)
			formMethods.setValue("defaultCode", defaultCodeProfileData.defaultCode)
			if (defaultCodeProfileData.defaultCode && defaultCodeProfileData.defaultCode === 'new-rate') {
				formMethods.setValue("rate", defaultCodeProfileData.rate)
				formMethods.setValue("description", defaultCodeProfileData.description)
			} else {
				formMethods.setValue("rate", null)
				formMethods.setValue("description", "")
			}
		}
	}, [defaultCodeProfileData])

	const {
		isPending: isPendingRemittanceFrequencyDDL,
		data: remittanceFrequencyDDL,
	} = useGetRemittanceFrequencyDDL();


	const updatedRemittanceFrequencyDDL = useMemo(() => {
		if (!remittanceFrequencyDDL) return [];

		const filteredRemittanceFrequencyDDL = remittanceFrequencyDDL.filter(
			(item: any) => item.label !== "Threshold 1" && item.label !== "Threshold 2"
		);

		return filteredRemittanceFrequencyDDL.map((item: any) => {
			switch (item.label) {
				case "Regular":
					return { ...item, label: "Monthly" };
				default:
					return item;
			}
		});
	}, [remittanceFrequencyDDL]);


	// remittanceFrequencyDDL = [ { "label": "Quarterly", "value": "682c7edc2e9342bfbf76c9dd" }, { "label": "Regular", "value": "682c80e4561a8d3349bcd85f" }, { "label": "Threshold 1", "value": "682c819747f56c520f0058b3" }, { "label": "Threshold 2", "value": "682c81e447f56c520f0058b5" } ] const updatedRemittanceFrequencyDDL = useMemo(() => { if (remittanceFrequencyDDL) { } }, [remittanceFrequencyDDL]) i want to change label Regular with Monthly and Threshold 1 with Semimonthly and Threshold 2 with Weekly

	// const {
	// 	isPending: isPendingDefaultCodeDDL,
	// 	data: defaultCodeDDL,
	// } = useGetDefaultCodeDDL(clientIdStore,ontarioId);
	const {
		isPending: isPendingDefaultCodeDDL,
		data: defaultCodeDDL,
	} = useGetDefaultCodeDDL(clientIdStore, ontarioId, taxYear);

	const updateddefaultCodeDDL = useMemo(() => {
		if (defaultCodeDDL) {
			if (defaultCodeDDL && Array.isArray(defaultCodeDDL)) {
				let defaultCode: IDropDownListResponse[] = sortDDLByLabel(defaultCodeDDL).map(
					(defaultCodeItem: IDropDownListResponse) => ({
						label: defaultCodeItem.label,
						value: defaultCodeItem.value,
					})
				);
				defaultCode.unshift({
					label: "New Rate",
					value: "new-rate",
				});
				return defaultCode;
			}
		}
	}, [defaultCodeDDL])



	const { isPending: isSubmitting, mutate: submitWSIBOntario } = useMutation({
		mutationFn: WSIBOntarioService.create,

		onSuccess: (res: IWSIBOntarioRemittance | IServerError | IResponse | any) => {


			if (res && res.status === 201) {
				queryClient.invalidateQueries({
					queryKey: [WSIB_ONTARIO_LIST_QUERY_KEY],
				});
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { isPending: isPendingDefaultCodeSubmit, mutate: submitWSIBOntarioDefaultCodeValues } = useMutation({
		mutationFn: WSIBOntarioService.createDefaultCode,

		onSuccess: (res: IWSIBOntarioRemittance | IServerError | IResponse | any) => {


			if ((res && res.status === 201) || (res && res.status === 200)) {
				// queryClient.invalidateQueries({
				// 	queryKey: ["default-code-ddl"],
				// });
				queryClient.invalidateQueries({
					queryKey: ["saved-wsib-ddl"],
				});
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				// setSelectedDefaultCodeProfile(res.data.id)
				setLoadedProfileId(res.data.id)
				formMethods.setValue('savedWSIBDDL', res.data.id)
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const { message, componentState } = useSelector(
		(state: IAppState) => state.viewState
	);

	// const { accountantList } = useSelector(
	//   (state: IAppState) => state.accountant
	// );

	const [openDialog, setDialogOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState([]);
	const [statusValue, setStatusValue] = useState("");
	const [apiAction, setApiAction] = useState("");
	const [emptyList, setEmptyList] = useState(false);
	const [searchText, setSearchText] = useState({});
	const [zeroWSIBOntario, setzeroWSIBOntario] = useState(true);
	const [rqRamittanceSetupUpdatedItems, setWSIBOntarioSetupUpdatedItems] =
		useState<any>(undefined);

	// for delete row
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [rqRamittanceRowData, setWSIBOntarioRowData] = useState<any>(0);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "firstName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const onSearchButtonClicked = () => {
		setQueryParams(searchText);
		setzeroWSIBOntario(false);
	};

	const { isPending: isDeleting, mutate: deleteWSIBOntarioById } =
		useMutation({
			mutationFn: WSIBOntarioService.delWSIBOntario,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the WSIBOntario fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					queryClient.invalidateQueries({
						queryKey: ["default-code-ddl"],
					});
					formMethods.setValue("rate", null)
					formMethods.setValue("effectiveDate", null)
					formMethods.setValue("defaultCode", 'new-rate')
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				return queryClient.invalidateQueries({
					queryKey: [WSIB_ONTARIO_LIST_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteDepartmentsField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				id: selectedDefaultCodeProfile,
				navigateToLogout: navigateToLogout,
			};
			deleteWSIBOntarioById(delPayload);
			setDialogOpen(false);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setWSIBOntarioRowData(params);
	}, []);

	const onSavedDefaultCodeSelectionChangeHandler = useCallback((params: any) => {
		setSelectedDefaultCodeProfile(params.value)
	}, []);

	const onDefaultCodeDeleteClicked = useCallback(() => {
		setDialogOpen(true);
		setSingleDelete(true);
	}, []);

	const onRowEditClicked = useCallback((params: any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/rqRamittance/setup/${params.id}`;
		navigate(path);
	}, []);

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const callAction = () => {
		if (apiAction === "delete") {
			alert(`Delete row with ID: ${selectedIds} has been clicked!`);
			// TODO api call for delete
		} else if (apiAction === "updateStatus") {
			alert(`action need to trigger : ${apiAction}`);
			// TODO api call for update status
		}
		setDialogOpen(false);
	};

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const selectionToolbar = useCallback((selectedItems: any) => {
		setSelectedIds(selectedItems);

		if (selectedItems.length) {
			return (
				// <Box sx={{ marginTop: theme.spacing(1) }}>
				// 	<Button
				// 		onClick={() => onRowMultipleDeleteClicked(selectedItems)}
				// 		variant="contained"
				// 		color="error"
				// 	>
				// 		Delete
				// 	</Button>
				// </Box>
				<></>
			);
		}
		return (
			// <Box sx={{ marginTop: theme.spacing(1) }}>
			// 	<RouterLinkButton to="/rqRamittance/setup" text="Add WSIBOntario" />
			// </Box>
			<></>
		);
	}, []);
	const updatedWSIBOntarioRamittanceSetupItems = useMemo(() => {
		if (wsibOntarioRemittanceSetup && wsibOntarioRemittanceSetup.items) {
			const items = [...wsibOntarioRemittanceSetup.items];

			// Find the last item with a non-null remitAmount using binary search
			let left = 0;
			let right = items.length - 1;
			let lastRemittedIndex = -1;

			while (left <= right) {
				let mid = Math.floor((left + right) / 2);
				if (items[mid].remitAmount != null) {
					lastRemittedIndex = mid;
					left = mid + 1; // look for later remitted value
				} else {
					right = mid - 1;
				}
			}

			// Find the first null remitAmount AFTER the last remitted one
			let nextNullIndex = -1;
			for (let i = lastRemittedIndex + 1; i < items.length; i++) {
				if (items[i].remitAmount == null) {
					nextNullIndex = i;
					break;
				}
			}
			// Map and add the two tags
			return items.map((item, index) => ({
				...item,
				// canDelete:
				// 	items[0].remitAmount === null ? false : index === lastRemittedIndex,
				// canEdit: items[0].remitAmount === null ? true : index === nextNullIndex,
				canDelete: index === lastRemittedIndex,
				canEdit: index === nextNullIndex,
			}));
		}
	}, [wsibOntarioRemittanceSetup]);



	const expandedLabels = ["WCB/WSIB", "Ontario"];
	const expandedWSIBOntarioRamittanceSetupOrSetupItem = "Remittance";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Ontario" &&
				item.label === expandedWSIBOntarioRamittanceSetupOrSetupItem
			) {
				isExpanded = true;
			}
			let itemPath = item.path;

			if (
				parentLabel === "Ontario"
				&& item.label === expandedWSIBOntarioRamittanceSetupOrSetupItem
			) {
				itemPath = `/taxes/wsib/ontario-remittance`;
			}
			const enhancedItem = {
				...item,
				expanded: isExpanded,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: itemPath
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: WSIB_ONTARIO_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));


		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);

	const onSubmitButtonClickedHandler = () => {


		const updatePayloadData =
			rqRamittanceSetupTableData &&
			rqRamittanceSetupTableData.map((item: any) => ({
				...item,
				chequeDate: formatDate(item.chequeDate),
			}));

		const EditedRowData = updatePayloadData.filter((item: any) => item.canEdit)
		submitWSIBOntario({ data: [EditedRowData[0]] })
	};

	const onSubmit = (values: any) => {

		// let payload = {
		// 	id: id && id,
		// 	rate: values.rqRate,
		// 	effectiveDate: values.effectiveDate && formatDate(values.effectiveDate),
		// }

		// submitWSIBOntario(payload)


		// let payloadOn = {
		// 	id: id && id,
		// 	rate: values.businessStartedBeforeYear,
		// 	effectiveDate: values.totalGrossPaidInYear,
		// }
		// submitWSIBOntarioValues(payloadOn);

	};


	const onSubmitDefaultCodeHandler = () => {
		const values = formMethods.watch();
		if (values.defaultCode === 'new-rate' && values.rate === null) {
			setSnackbarData({
				open: true,
				message: "Rate is required",
				severity: "error"
			})
		} else if (values.defaultCode === 'new-rate' && values.description === null) {
			setSnackbarData({
				open: true,
				message: "Description is required",
				severity: "error"
			})
		} else if (!values.effectiveDate || values.effectiveDate === null) {
			setSnackbarData({
				open: true,
				message: "Effective Date is required",
				severity: "error"
			})
		} else {
			let payload = {
				id: defaultCodeProfileData && defaultCodeProfileData?.effectiveDate === formatDate(values.effectiveDate) ? defaultCodeProfileData.id : undefined,
				// rate: "121211111",
				// effectiveDate: "2024-11-11"
				clientId: clientIdStore,
				provinceId: ontarioId,
				defaultCode: values.defaultCode,
				description: values.description,
				rate: values.rate,
				effectiveDate: values.effectiveDate && formatDate(values.effectiveDate),
			}

			submitWSIBOntarioDefaultCodeValues(payload)
		}
	}


	const onRemittanceFrequencyChangeHandler = async (selectedItem: any) => {
		if (wsibOntarioSavedFrequency && wsibOntarioSavedFrequency.frequencies === null) {
			(async () => {
				try {
					const responce = await WSIBOntarioService.createWSIBOntarioFrequency(
						selectedItem.value,
						clientIdStore,
						taxYear,
						loadedProfileId,
						navigateToLogout
					)

				}
				catch {

				} finally {
					queryClient.invalidateQueries({
						queryKey: [
							'rq-remittance-saved-frequency'
						],
					});
					setRemittanceFrequencyId(selectedItem.value);
					queryClient.invalidateQueries({
						queryKey: [
							WSIB_ONTARIO_LIST_QUERY_KEY,
							selectedItem.value,
							clientIdStore,
							taxYear,
							loadedProfileId,
						],
					});
				}
			})();
		} else if (selectedItem.value) {
			// setRemittanceFrequencyId(selectedItem.value);
			// queryClient.invalidateQueries({
			// 	queryKey: [
			// 		WSIB_ONTARIO_LIST_QUERY_KEY,
			// 		clientIdStore,
			// 		selectedItem.value,
			// 		managedBy,
			// 		taxYear,
			// 	],
			// });
			(async () => {
				try {
					const responce = await WSIBOntarioService.createWSIBOntarioFrequency(
						selectedItem.value,
						clientIdStore,
						taxYear,
						loadedProfileId,
						navigateToLogout
					)

				}
				catch {

				} finally {
					setRemittanceFrequencyId(selectedItem.value);
					queryClient.invalidateQueries({
						queryKey: [
							WSIB_ONTARIO_LIST_QUERY_KEY,
							selectedItem.value,
							clientIdStore,
							taxYear,
							loadedProfileId,
						],
					});
				}
			})();
		}
	};

	const onSavedWSIBChangeHandler = async (event: any) => {
		if (!event.value) {
			formMethods.setValue("rate", null)
			formMethods.setValue("description", "")
			formMethods.setValue("effectiveDate", null)
			formMethods.setValue("defaultCode", 'new-rate')
			setLoadedProfileId(event.value)
		} else {
			(async () => {
				try {
					const responce = await WSIBOntarioService.createWSIBOntarioFrequency(
						remittanceFrequencyId,
						clientIdStore,
						taxYear,
						event.value,
						navigateToLogout
					)

				}
				catch {

				} finally {
					queryClient.invalidateQueries({
						queryKey: [
							'rq-remittance-saved-frequency'
						],
					});
					setLoadedProfileId(event.value)
				}
			})();
		}
	}

	// getting latest updated values of form fields using watch method
	const { watch } = formMethods;
	const values = watch([
		"remittanceFrequencyId",
	]);

	const savePdfAsFile = (xmlData: any, filename: any) => {
		const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
		FileSaver.saveAs(blob, filename);
	};
	const onExportButtonClick = async () => {
		const [remittanceFrequencyId] = values;
		try {
			setIsLoading(true);
			const response = await WSIBOntarioService.exportWSIBOntarioRemittanceExcelReport(
				clientIdStore,
				remittanceFrequencyId,
				taxYear,
				loadedProfileId,
			);

			if (response) {
				if (response.status === 400) {
					setSnackbarData({
						open: true,
						message: response.data.message
							? response.data.message
							: "Something went wrong.",
						severity: "error",
					});
				} else {
					const filename = `WSIB Ontario Remittance.xlsx`;
					savePdfAsFile(response, filename);
				}
			} else {
				console.error("Error: No data received from the server");
			}
		} catch (err) {
			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}

				sx={{
					mt: "1rem"
				}}>
				<div
					style={{
						margin: "15px 0",
						fontSize: "1.5rem",
						fontWeight: "bold",
						fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
					}}
				>
					{"Remittance"}
				</div>
				<BackdropCircularProgress
					open={isPending || isPendingRemittanceFrequencyDDL || isSubmitting || isPendingWSIBOntarioSavedFrequency || isPendingDefaultCodeDDL || isPendingDefaultCodeSubmit || pendingDefaultCodeProfileDAta || isPendingSavedWSIBDDL || isLoading}
					testId="rqRamittance-list"
				/>
				<DeleteConfirmationDialog
					open={openDialog}
					deleteActionHandler={deleteDepartmentsField}
					cancelActionHandler={onCloseHandler}
					testId="wsib-remittance-confirmation-dialog"
				/>
				<FormProvider {...formMethods}>
					<form onSubmit={formMethods.handleSubmit(onSubmit)}>
						<WSIBOntarioRemittanceHeader
							defaultCodeDDL={updateddefaultCodeDDL ?? []}
							onSubmitDefaultCodeHandler={onSubmitDefaultCodeHandler}
							onRemittanceFrequencyChangeHandler={onRemittanceFrequencyChangeHandler}
							remittanceFrequencyDDL={updatedRemittanceFrequencyDDL}
							onDefaultCodeDeleteClicked={onDefaultCodeDeleteClicked}
							onSavedDefaultCodeSelectionChangeHandler={onSavedDefaultCodeSelectionChangeHandler}
							wsibOntarioSavedFrequency={wsibOntarioSavedFrequency}
							savedWSIBDDL={savedWSIBDDL}
							formMethods={formMethods}
							onSavedWSIBChangeHandler={onSavedWSIBChangeHandler}
							setSelectedDefaultCodeProfile={(value: any) => setSelectedDefaultCodeProfile(value)}
							wsibOntarioRemittanceSetup={wsibOntarioRemittanceSetup && wsibOntarioRemittanceSetup}
							onExportButtonClick={onExportButtonClick}
							defaultCodeProfileData={defaultCodeProfileData}

						/>
					</form>
				</FormProvider>

				<WSIBOntarioRemittanceDataGrid
					columns={wsibOntarioRemittanceSetup ? wsibOntarioRemittanceSetup.headerColumns : []}
					rows={
						wsibOntarioRemittanceSetup && updatedWSIBOntarioRamittanceSetupItems
							? updatedWSIBOntarioRamittanceSetupItems
							: []
					}
					showIndexColumn={true}
					showChecboxColumn={isAdminUser === 400 ? true : false}
					// showSettingColumn={true}
					// onRowEditClicked={onRowEditClicked}
					onRowDeleteClicked={onRowDeleteClicked}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						wsibOntarioRemittanceSetup && wsibOntarioRemittanceSetup.total
							? wsibOntarioRemittanceSetup.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					selectionToolbar={selectionToolbar}
					selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
					searchFieldPlaceholder={"Search..."}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroWSIBOntario(value)
					}
					pageTitle={"WSIB Remittance"}
					rqRamittanceSetupTableData={(data: any) =>
						setWSIBOntarioSetupTableData(data)
					}
					tableMargin={"0"}
				/>



				{wsibOntarioRemittanceSetup && wsibOntarioRemittanceSetup.items && (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							// backgroundColor: "#fff",
							backgroundColor: "#F7F7F7",
							// marginTop: theme.spacing(3),
							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					>
						<Toolbar>
							<EnhancedButtonPrimeReact
								label="Submit"
								// type="submit"
								onClickHandler={onSubmitButtonClickedHandler}
								// icon="pi pi-check"
								raised
								severity={EnhancedButtonPrimeReactSeverity.Success}
							/>
						</Toolbar>
					</AppBar>
				)}
			</Grid>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
